// =============================================================================
// humanize() — convert snake_case / kebab-case / camelCase values to Title Case
// for display purposes.
//
// Examples:
//   humanize('parent_transcript')      → 'Parent Transcript'
//   humanize('in_progress')            → 'In Progress'
//   humanize('extremely_high')         → 'Extremely High'
//   humanize('hispanic')               → 'Hispanic'
//   humanize('150k_to_200k')           → '150k To 200k'
//   humanize('spanish_conversational') → 'Spanish Conversational'
//   humanize(null)                     → ''
//
// Special cases that need a different display form than the default
// snake-to-title transformation get an override map below.
// =============================================================================

const OVERRIDES: Record<string, string> = {
  // ---- Known abbreviations (preserve capitalization in tags / labels) ----
  'hsf': 'HSF',
  'hsf_yli_alum': 'HSF YLI Alum',
  'yli': 'YLI',
  'hpu': 'HPU',
  'uncc': 'UNCC',
  'ncstate': 'NC State',
  'unc': 'UNC',
  'nc_resident': 'NC Resident',
  'hpu_target': 'HPU Target',
  'uncc_target': 'UNCC Target',
  'ncstate_target': 'NC State Target',
  'charlotte_resident': 'Charlotte Resident',
  'hsf_yli': 'HSF YLI',
  'hslda': 'HSLDA',
  'nche': 'NCHE',
  'fftc': 'FFTC',
  'nccf': 'NCCF',
  'apa': 'APA',
  'aata': 'AATA',
  'us': 'US',
  // ---- Income brackets ----
  'under_30k': 'Under $30k',
  '30k_to_60k': '$30k–$60k',
  '60k_to_100k': '$60k–$100k',
  '100k_to_150k': '$100k–$150k',
  '150k_to_200k': '$150k–$200k',
  'over_200k': 'Over $200k',
  // ---- Test score keys ----
  'act': 'ACT',
  'sat': 'SAT',
  'psat': 'PSAT',
  // ---- Application types ----
  'auto_considered': 'Auto-considered with admission',
  'separate_application': 'Separate application',
  'requires_admission_first': 'Requires admission first',
  // ---- Amount type ----
  'full_ride': 'Full Ride',
  // ---- Competitiveness ----
  'extremely_high': 'Extremely High',
  // ---- School types ----
  'public_in_state': 'Public (In-State)',
  'public_out_of_state': 'Public (Out-of-State)',
  // ---- Effort estimates ----
  'S': 'Small',
  'M': 'Medium',
  'L': 'Large',
  // ---- Schooling types ----
  'homeschool_npns': 'Homeschool (NC NPNS)',
  // ---- CC role ----
  'student_only': 'Student Only',
  // ---- Result enum ----
  'awarded': 'Awarded',
  'rejected': 'Rejected',
  'waitlisted': 'Waitlisted',
  // ---- Match strength ----
  'high': 'High',
  'medium': 'Medium',
  'low': 'Low',
  // ---- Languages ----
  'english': 'English',
  'spanish_conversational': 'Spanish (Conversational)',
  'spanish_fluent': 'Spanish (Fluent)',
  // ---- Religious affiliations ----
  'lutheran': 'Lutheran',
  'catholic': 'Catholic',
  'protestant': 'Protestant',
  'evangelical': 'Evangelical',
  'none': 'None',
  // ---- Ethnicities ----
  'hispanic': 'Hispanic / Latino',
  'white': 'White',
  'black': 'Black / African American',
  'asian': 'Asian',
  'native_american': 'Native American',
  'pacific_islander': 'Pacific Islander',
  'middle_eastern': 'Middle Eastern',
  'two_or_more_races': 'Two or More Races',
  // ---- Citizenship ----
  'us_citizen': 'US Citizen',
  'us_permanent_resident': 'US Permanent Resident',
  'daca': 'DACA',
  'international': 'International',
  // ---- Degree levels ----
  'high_school': 'High School',
  'some_college': 'Some College',
  'associates': "Associate's",
  'bachelors': "Bachelor's",
  'masters': "Master's",
  'doctorate': 'Doctorate',
  // ---- Common tag values ----
  'female': 'Female',
  'male': 'Male',
  'homeschool': 'Homeschool',
  'christian': 'Christian',
  'classical_education': 'Classical Education',
  'art_therapy_track': 'Art Therapy Track',
  'psychology_major': 'Psychology Major',
  'studio_art_major': 'Studio Art Major',
  'dual_enrollment': 'Dual Enrollment',
  'orchestra': 'Orchestra',
  'community_service': 'Community Service',
};

/**
 * Convert a snake_case / kebab-case / lowercase value into a human-readable
 * Title Case label. Honors a small override map for special cases.
 *
 * Returns '' for null/undefined/empty inputs.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  if (OVERRIDES[value]) return OVERRIDES[value];

  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => {
      if (!word) return '';
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Humanize a list of values into a comma-separated display string.
 */
export function humanizeList(values: Array<string | null | undefined> | undefined): string {
  if (!values || values.length === 0) return '';
  return values.map(humanize).filter(Boolean).join(', ');
}
